import { Col, Form } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { blankToUndefined, optionalNonNegativeValidator } from "components/Form/components/Validations/validationRules/NonNegative";
import i18n from "i18next";
import { type FC, useMemo } from "react";

import type { SiteBatchUpdateEntity } from "src/app/features/Site/types/SiteBatchUpdateEntity";
import { Checkbox, SelectDynamic, TextTemplating } from "../../Form/formComponents";
import { NumberInput } from "../../Form/formComponents/Number/NumberInput";
import type { FormConfigurationEntity } from "../../Form/types/FormConfigurationEntity";
import type { SearchFormProps } from "../../Form/types/types";
import { productProfileSelectConfig, siteGroupsSelectConfig } from "../../SearchForm/FormSearchUtils";
import { DrawerForm } from "../DrawerForm";
import { BatchUpdateFormRow } from "../drawerFormStyles";
import { FieldLabel } from "../FieldLabel";

type OrgUnitBatchUpdateFormProps = SearchFormProps<SiteBatchUpdateEntity> & {
    form?: FormInstance;
    formDefinition?: FormConfigurationEntity;
};

// Field names as they appear in the Site/Site Group form config from BE, where they differ from our SiteBatchUpdateEntity keys
// (see siteAndSiteGroupUpdateFields in SiteAndSiteGroupUtils.ts for the same terminalParameters.* nesting).
const ORG_UNIT_FORM_FIELD_NAME_OVERRIDES: Partial<Record<keyof SiteBatchUpdateEntity, string>> = {
    eventLog: "terminalParameters.eventLog",
    headerLines: "terminalParameters.headerLines",
    footerLines: "terminalParameters.footerLines",
    footerMandatStation: "terminalParameters.posFooter",
    tankLimit: "terminalParameters.tankLimit",
    nonstop: "terminalParameters.nonstop",
};

// Shared by Site and Site Group batch update forms; the two entities only differ in which BE form config
// ("site" / "site-group") governs field availability, so callers fetch it and pass it in via `formDefinition`.
export const OrgUnitBatchUpdateForm: FC<OrgUnitBatchUpdateFormProps> = ({ searchCallback, form, formDefinition }) => {
    // Only expose fields that exist in the current Data Group's form config (varies by BP/IPASO/MADIC/APAC).
    const availableFieldNames = useMemo(
        () => new Set(formDefinition?.formConfiguration?.tabs.flatMap((tab) => tab.fields.map((field) => field.name)) ?? []),
        [formDefinition],
    );

    const isFieldAvailable = (fieldName: keyof SiteBatchUpdateEntity) =>
        availableFieldNames.has(ORG_UNIT_FORM_FIELD_NAME_OVERRIDES[fieldName] ?? fieldName);

    return (
        <DrawerForm onSubmit={searchCallback} form={form} useSubmitButton={true} submitLabel={i18n.t("button.batchUpdate")}>
            <p>{i18n.t("batchUpdate.message")}</p>

            {isFieldAvailable("parentUnit") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitsSearchForm.criteriaBadges.parentSiteId")} isRequiredField />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="parentUnit"
                                dependencies={["updateParent"]}
                                rules={[
                                    ({ getFieldValue }) => ({
                                        required: getFieldValue("updateParent"),
                                        message: i18n.t("common.input.required.error"),
                                    }),
                                ]}
                            >
                                <SelectDynamic name="parentUnit" label={""} fieldConfig={siteGroupsSelectConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateParent" valuePropName="checked">
                                <Checkbox name="updateParent" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("productProfileId") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.productProfile")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="productProfileId">
                                <SelectDynamic name="productProfileId" label={""} fieldConfig={productProfileSelectConfig} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateProductProfile" valuePropName="checked">
                                <Checkbox
                                    name="updateProductProfile"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("eventLog") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.eventLog")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="eventLog" valuePropName="checked">
                                <Checkbox name="eventLog" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateEventLog" valuePropName="checked">
                                <Checkbox name="updateEventLog" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("headerLines") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.headerLines")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="headerLines">
                                <TextTemplating name="headerLines" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateHeaderLines" valuePropName="checked">
                                <Checkbox
                                    name="updateHeaderLines"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("footerLines") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.footerLines")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="footerLines">
                                <TextTemplating name="footerLines" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateFooterLines" valuePropName="checked">
                                <Checkbox
                                    name="updateFooterLines"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("footerMandatStation") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.posFooter")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="footerMandatStation">
                                <TextTemplating name="footerMandatStation" label={""} readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updatePosFooter" valuePropName="checked">
                                <Checkbox name="updatePosFooter" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("tankLimit") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.tankLimit")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item
                                name="tankLimit"
                                rules={[{ validator: optionalNonNegativeValidator }]}
                                getValueFromEvent={blankToUndefined}
                            >
                                <NumberInput name="tankLimit" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateTankLimit" valuePropName="checked">
                                <Checkbox name="updateTankLimit" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("nonstop") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.terminalParameters.nonstop")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="nonstop" valuePropName="checked">
                                <Checkbox name="nonstop" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateNonstop" valuePropName="checked">
                                <Checkbox name="updateNonstop" label="batchUpdate.updateField" readOnly={false} defaultChecked={false} />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("acousticNotification") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.acousticNotification")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="acousticNotification" valuePropName="checked">
                                <Checkbox name="acousticNotification" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateAcousticNotification" valuePropName="checked">
                                <Checkbox
                                    name="updateAcousticNotification"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}

            {isFieldAvailable("acousticNotificationOfTransactionResult") && (
                <>
                    <FieldLabel label={i18n.t("orgUnitEditForm.acousticNotificationOfTransactionResult")} />
                    <BatchUpdateFormRow>
                        <Col span={16}>
                            <Form.Item name="acousticNotificationOfTransactionResult" valuePropName="checked">
                                <Checkbox name="acousticNotificationOfTransactionResult" label="" readOnly={false} />
                            </Form.Item>
                        </Col>
                        <Col span={4}>
                            <Form.Item name="updateAcousticNotificationOfTransactionResult" valuePropName="checked">
                                <Checkbox
                                    name="updateAcousticNotificationOfTransactionResult"
                                    label="batchUpdate.updateField"
                                    readOnly={false}
                                    defaultChecked={false}
                                />
                            </Form.Item>
                        </Col>
                    </BatchUpdateFormRow>
                </>
            )}
        </DrawerForm>
    );
};
